import {
  MentorshipDetails,
  SuggestedTopic,
  SuggestedTopicsResponse,
} from "@/lib/api/mentorships";
import * as React from "react";
import { ChevronRight, ChevronDown, Loader2 } from "lucide-react";
import { usePostHogEvent } from "@/lib/posthog";
import { useMessageInputStore } from "@/lib/hooks/conversations/use-message-input";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";

interface SuggestedTopicsContentProps {
  data: SuggestedTopicsResponse;
  mentorship?: MentorshipDetails;
}

export const SuggestedTopicsContent = React.forwardRef<
  HTMLUListElement,
  SuggestedTopicsContentProps
>(({ data, mentorship }, ref) => {
  const { setMessageInput, messageInputs } = useMessageInputStore();
  const { currentConversation } = useCurrentConversation();
  const { dispatchEvent: trackSuggestedTopicsEvent } = usePostHogEvent(
    "suggested_topics_viewed",
    {
      onMount: false,
    },
  );
  React.useEffect(() => {
    if (data && data.topics && data.topics.length > 0) {
      trackSuggestedTopicsEvent("suggested_topics_success", {
        mentorship_id: mentorship?.id,
        mentor_id: mentorship?.mentor?.id,
        mentee_id: mentorship?.mentee?.id,
        topics_count: data.topics.length,
        topics: data.topics.map((topic) => topic.topic).join(","),
      });
    } else {
      trackSuggestedTopicsEvent("suggested_topics_failure", {
        mentorship_id: mentorship?.id,
        mentor_id: mentorship?.mentor?.id,
        mentee_id: mentorship?.mentee?.id,
      });
    }
  }, [data, mentorship]);

  const [expandedTopic, setExpandedTopic] = React.useState<number | null>(null);

  const toggleTopic = (index: number) => {
    const currentExpanded = expandedTopic;
    if (currentExpanded !== index) {
      trackSuggestedTopicsEvent("suggested_topics_expand", {
        mentorship_id: mentorship?.id,
        mentor_id: mentorship?.mentor?.id,
        mentee_id: mentorship?.mentee?.id,
        topic: data.topics[index].topic,
      });
    }
    setExpandedTopic(currentExpanded === index ? null : index);
  };

  // Check if topics array is empty or null
  if (!data.topics || data.topics.length === 0) {
    return (
      <div className="py-4 text-center">
        <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-gray-400" />
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <hr />
      <ul ref={ref} className="flex flex-col">
        {data.topics.map((item: SuggestedTopic, index: number) => {
          const isExpanded = expandedTopic === index;

          // Hide this item if another topic is expanded
          if (expandedTopic !== null && expandedTopic !== index) {
            return null;
          }

          return (
            <li
              key={index}
              className={`my-2 rounded-md ${!isExpanded ? "hover:bg-gray-50" : ""}`}
            >
              <div
                className={`flex cursor-pointer items-center justify-between px-3 py-2`}
                onClick={() => toggleTopic(index)}
              >
                <div
                  className={`inline-block rounded-full px-3 py-1 text-sm text-gray-500 ${
                    isExpanded ? "border border-gray-200" : ""
                  }`}
                >
                  {item.topic}
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
              </div>

              {isExpanded && (
                <div className="px-3 pb-2">
                  {item.context && (
                    <div className="mt-3">
                      <div className="font-medium">Context:</div>
                      <p className="text-sm text-gray-500">{item.context}</p>
                    </div>
                  )}

                  {item.mentee_angle && (
                    <div className="mt-3">
                      <div className="font-medium">
                        Why it matters (mentor tip):
                      </div>
                      <p className="text-sm text-gray-500">
                        {item.mentee_angle}
                      </p>
                    </div>
                  )}

                  {item.mentee_tip && (
                    <div
                      className="mt-3 cursor-pointer rounded-md bg-gray-50 p-3 hover:shadow-md"
                      onClick={() => {
                        setMessageInput(
                          currentConversation || "",
                          `${messageInputs[currentConversation || ""] || ""} ${
                            item.mentee_tip
                          }`,
                        );
                      }}
                    >
                      <div className="font-medium">Suggested opener:</div>
                      <p className="text-sm text-gray-500">{item.mentee_tip}</p>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
});

SuggestedTopicsContent.displayName = "SuggestedTopicsContent";
